// Récapitulatif d'un inventaire (canvas W10 et W10b). Décisions 26, 28, 30 et 40.
// Deux groupes : les écarts au dernier inventaire (« Pendant le séjour » pour une sortie comparée
// à l'entrée), et les objets déjà absents au dernier inventaire par rapport au modèle,
// information pour l'équipe, jamais attribuée au vacancier.
// Le verdict Validé ou Contesté n'existe que pour une sortie comparée à une entrée :
// sinon, personne à qui attribuer un écart, le bandeau reste neutre.

import { isGap, isMissingAtReference, objectCount, missingLabel, referenceWhen, plural } from './fieldReference'

// Quantité de référence : « Entrée 6 → Sortie 4 », « Prévu 10 → Entrée 8 »
function referenceShort(reference, type)
{
	if (reference.source !== 'inventory')
		return 'Prévu'
	if (reference.type === 'arrival')
		return type === 'arrival' ? 'Entrée précédente' : 'Entrée'
	if (reference.type === 'departure')
		return type === 'departure' ? 'Sortie précédente' : 'Dernière sortie'
	return 'Dernier inventaire'
}

function currentShort(type)
{
	if (type === 'arrival')
		return 'Entrée'
	if (type === 'departure')
		return 'Sortie'
	return 'Compté'
}

function missingSuffix(reference)
{
	if (reference.type === 'arrival')
		return 'à l\'entrée'
	if (reference.type === 'departure')
		return 'à la dernière sortie'
	return 'au dernier inventaire'
}

// « … du 12 oct. » finit déjà par un point, « … du 3 mai » non
function sentence(text)
{
	return /\.$/.test(text) ? text : text + '.'
}

function gapBadge(diff)
{
	const count = Math.abs(diff)
	if (diff < 0)
		return '−' + count + (count > 1 ? ' manquants' : ' manquant')
	return '+' + count + ' en trop'
}

export function isStayVerdict(type, reference)
{
	return type === 'departure' && reference.source === 'inventory' && reference.type === 'arrival'
}

export function summarize(type, themes, counts, reference, now)
{
	const fromLabel = referenceShort(reference, type)
	const toLabel = currentShort(type)
	const gaps = []
	const missing = []
	let total = 0
	themes.forEach(theme => {
		theme.objects.forEach(object => {
			total++
			const ref = reference.counts[object.id]
			if (isGap(object, counts, reference))
			{
				const count = counts[object.id]
				gaps.push({ object, theme, line: fromLabel + ' ' + ref + ' → ' + toLabel + ' ' + count, badge: gapBadge(count - ref), diff: count - ref })
			}
			if (isMissingAtReference(object, reference))
			{
				missing.push({ object, theme, line: 'Prévu ' + object.expected + ' → ' + fromLabel + ' ' + ref, badge: '−' + (object.expected - ref) + ' ' + missingSuffix(reference) })
			}
		})
	})

	const stayVerdict = isStayVerdict(type, reference)
	const fromInventory = reference.source === 'inventory'
	let gapsTitle = 'Par rapport au modèle'
	if (stayVerdict)
		gapsTitle = 'Pendant le séjour'
	else if (fromInventory)
		gapsTitle = 'Depuis le dernier inventaire'

	let likeReference = 'conforme au modèle'
	if (stayVerdict)
		likeReference = 'comme à l\'entrée'
	else if (fromInventory)
		likeReference = 'comme au dernier inventaire'
	const others = total - gaps.length
	let othersText = null
	if (gaps.length > 0 && others === 1)
		othersText = 'L\'autre objet est ' + likeReference + '.'
	else if (gaps.length > 0 && others > 1)
		othersText = 'Les ' + others + ' autres objets sont ' + likeReference.replace('conforme', 'conformes') + '.'

	let verdict = null
	let banner = null
	if (stayVerdict && gaps.length === 0)
	{
		verdict = 'validated'
		banner = { tone: 'ok', title: 'Validé', text: sentence('Rien n\'a changé depuis l\'' + referenceWhen(reference, now)) }
	}
	else if (stayVerdict)
	{
		verdict = 'disputed'
		banner = { tone: 'gap', title: 'Contesté', text: plural(gaps.length, 'écart') + ' pendant le séjour', hint: 'Seuls les écarts par rapport à l\'entrée comptent pour le verdict.' }
	}
	else if (gaps.length === 0)
		banner = { tone: 'ok', title: 'Aucun écart', text: fromInventory ? sentence('Tout est comme au dernier inventaire : ' + referenceWhen(reference, now)) : 'Tout est conforme au modèle de l\'hébergement.' }
	else
		banner = { tone: 'gap', title: plural(gaps.length, 'écart'), text: fromInventory ? sentence('Depuis le dernier inventaire : ' + referenceWhen(reference, now)) : 'Par rapport au modèle de l\'hébergement.' }

	return {
		total,
		gaps,
		missing,
		verdict,
		banner,
		gapsTitle,
		othersText,
		missingTitle: missingLabel(reference),
		missingHint: type === 'arrival' || type === 'departure' ? 'Pour l\'équipe, non attribué au vacancier.' : 'Information pour l\'équipe.'
	}
}

// « Julie R. » et « JR » pour l'utilisateur connecté ; un autre auteur reste anonyme tant que
// le serveur ne renvoie pas les noms des contributeurs (lot 5, fil d'activité)
export function authorOf(userId, user)
{
	if (!user || !userId || userId !== user._id)
		return { name: 'Un collègue', initials: '?' }
	const firstname = (user.firstname || '').trim()
	const lastname = (user.lastname || '').trim()
	const name = (firstname + (lastname ? ' ' + lastname.charAt(0).toUpperCase() + '.' : '')).trim() || 'Moi'
	const initials = ((firstname.charAt(0) || '') + (lastname.charAt(0) || '')).toUpperCase() || '?'
	return { name, initials }
}

export function isMine(userId, user)
{
	return !!user && !!userId && userId === user._id
}

// Modifier son commentaire (décision 31) : l'ancien texte part dans l'historique
export function editComment(comment, text, now)
{
	return {
		...comment,
		comment: text,
		commentEditTime: now,
		commentHistory: (comment.commentHistory || []).concat([{ text: comment.comment, timestamp: comment.commentEditTime || comment.commentTime }])
	}
}

// Sous-titre et état « fait » des tuiles Commentaire et Photos
export function commentTile(during, after)
{
	if (during + after === 0)
		return { subtitle: 'Aucun pour l\'instant', done: false }
	if (after === 0)
		return { subtitle: plural(during, 'déjà saisi', 'déjà saisis'), done: false }
	return { subtitle: plural(during + after, 'commentaire'), done: true }
}

export function photoTile(during, after)
{
	if (during + after === 0)
		return { subtitle: 'Aucune pour l\'instant', done: false }
	if (after === 0)
		return { subtitle: plural(during, 'déjà prise', 'déjà prises'), done: false }
	return { subtitle: plural(during + after, 'photo'), done: true }
}
